import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getHealth,
  googleSignInUrl,
  loginWithPassword,
  registerAccount,
  peekInvitation,
  setToken,
  ApiError,
} from '../api';

/**
 * The sign-in screen (Phase 8 AC1, AC2).
 *
 * Google is the way in. The pasted-token form stays below it, collapsed, because it is
 * how the API gets driven by hand in development — but it is no longer the front door.
 */

/** An invitation link is `/invite/<token>`; the app reads it straight off the URL. */
export function invitationTokenFromUrl(pathname = window.location.pathname): string | null {
  const match = /^\/invite\/([^/]+)\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

/**
 * Why the last Google sign-in was turned away, when the callback sent the browser back here.
 *
 * The server sends a key rather than a sentence, so nobody can put words on this screen by
 * crafting a link; an unknown key reads as nothing at all.
 */
const SIGNIN_REFUSALS: Record<string, { title: string; body: string }> = {
  registration_closed: {
    title: 'There is no account here for that Google address.',
    body:
      'This planner takes new people by invitation only. Ask someone already here to invite ' +
      'you, then open the link they send. If you have an account under another address, sign ' +
      'in with that one.',
  },
  // The link was judged before anything was made, so nothing was; the address alone still opens
  // the door, which is why "continue without it" is true whenever this is the reason.
  invitation_unusable: {
    title: 'That invitation link can no longer be used.',
    body:
      'It may have been withdrawn, run out, or been sent to a different address. Nothing was ' +
      'changed. You can continue with Google without it, or sign in with the address it was ' +
      'sent to. To join that project, ask whoever invited you for a new link.',
  },
  // Lost the sign-up race: somebody made an account for this address a moment before. Nothing
  // was written, and the next sign-in finds that account through the ordinary path.
  try_again: {
    title: 'Your account was being created at the same moment.',
    body:
      'An account for this address was made while you were signing in, so nothing was changed. ' +
      'Please sign in again.',
  },
};

/**
 * Read once per page load, then taken off the address bar so a reload or a shared link does not
 * repeat it.
 *
 * Captured here rather than in an effect, because an effect runs twice under StrictMode and the
 * screen can remount while `/api/health` loads: whichever run came second found the address
 * already cleaned and put the notice away again, which is what the first e2e run of this showed.
 */
let refusalOnArrival: string | null | undefined;
function takeSignInRefusal(): string | null {
  if (refusalOnArrival === undefined) {
    const key = new URLSearchParams(window.location.search).get('signin_error');
    refusalOnArrival = key && key in SIGNIN_REFUSALS ? key : null;
    if (refusalOnArrival) window.history.replaceState(null, '', window.location.pathname);
  }
  return refusalOnArrival;
}

function InvitationBanner({ token }: { token: string }) {
  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => peekInvitation(token),
    retry: false,
  });

  if (preview.isPending) return <p className="muted">Checking that invitation…</p>;

  if (preview.error) {
    // Expired, withdrawn, already used and never-valid all read the same, because the
    // server deliberately does not distinguish them.
    return (
      <div className="notice warn">
        <strong>That invitation is no longer valid.</strong>
        <p className="muted">
          Ask whoever invited you to send a new link. You can still sign in below.
        </p>
      </div>
    );
  }

  return (
    <div className="notice">
      <strong>You have been invited to {preview.data!.projectTitle}</strong>
      <p className="muted">
        as {preview.data!.role}, at {preview.data!.email}. Sign in with that Google account to
        accept.
      </p>
    </div>
  );
}

export function SignIn({ onTokenSaved }: { onTokenSaved: () => void }) {
  const [invitation, setInvitation] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [value, setValue] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [mode, setMode] = useState<'signIn' | 'register'>('signIn');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [refusal] = useState(takeSignInRefusal);

  // What this deployment actually offers. A button that redirects to a provider which
  // can authenticate nobody is worse than no button, and a sign-up form on a closed
  // instance is worse than none.
  const health = useQuery({ queryKey: ['health'], queryFn: getHealth });
  const googleAvailable = health.data?.googleLogin ?? false;
  const registrationOpen = health.data?.registrationOpen ?? false;
  // An invitation is already permission to join, so it opens sign-up even where the
  // deployment is otherwise closed — the same rule the server applies.
  const canRegister = registrationOpen || invitation !== null;
  const registering = mode === 'register';

  const onSignedIn = () => {
    // The session cookie is set; a reload is the simplest way to bring the whole app up
    // as the signed-in user rather than reconciling every cached query by hand.
    window.location.assign('/');
  };
  const onFailed = (e: unknown) =>
    setLoginError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');

  const login = useMutation({
    mutationFn: () => loginWithPassword(email.trim(), password, rememberMe),
    onSuccess: onSignedIn,
    onError: onFailed,
  });

  const signUp = useMutation({
    mutationFn: () =>
      registerAccount({
        email: email.trim(),
        password,
        rememberMe,
        // The browser knows the zone; asking for it would be a worse first impression.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(invitation ? { invitation } : {}),
      }),
    onSuccess: onSignedIn,
    onError: onFailed,
  });

  const busy = login.isPending || signUp.isPending;

  useEffect(() => {
    setInvitation(invitationTokenFromUrl());
  }, []);

  return (
    <div className="card signin">
      <h2>Planner</h2>

      {refusal && (
        <div className="notice warn" role="alert" data-testid="signin-refusal">
          <strong>{SIGNIN_REFUSALS[refusal]!.title}</strong>
          <p className="muted">{SIGNIN_REFUSALS[refusal]!.body}</p>
        </div>
      )}

      {invitation && <InvitationBanner token={invitation} />}

      <p className="muted">Sign in to your own workspace. Nothing is shared until you share it.</p>

      {canRegister && (
        <div className="signin-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={!registering}
            className={registering ? 'tab' : 'tab active'}
            onClick={() => {
              setMode('signIn');
              setLoginError(null);
            }}
          >
            Sign in
          </button>
          <button
            role="tab"
            aria-selected={registering}
            className={registering ? 'tab active' : 'tab'}
            onClick={() => {
              setMode('register');
              setLoginError(null);
            }}
          >
            Create account
          </button>
        </div>
      )}

      <form
        className="signin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim() || !password) return;
          if (registering) signUp.mutate();
          else login.mutate();
        }}
      >
        <label>
          Email
          <input
            type="email"
            value={email}
            autoComplete="username"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            // Tells a password manager to offer a new one rather than an existing one.
            autoComplete={registering ? 'new-password' : 'current-password'}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {registering && <p className="muted small">At least 10 characters.</p>}

        <label className="check">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          Remember me on this device
        </label>

        {/* `primary`: the one control that commits what this form was opened to do, so the
            Google alternative below, drawn as a secondary, never outranks it once it is live. */}
        <button className="primary" type="submit" disabled={!email.trim() || !password || busy}>
          {busy ? 'Working…' : registering ? 'Create account' : 'Sign in'}
        </button>
        {loginError && <p className="error">{loginError}</p>}
      </form>

      {googleAvailable && <p className="muted or-divider">or</p>}

      {googleAvailable && (
        <a
          className="google-signin"
          href={googleSignInUrl({
            ...(invitation ? { invitation } : {}),
            // Land back on the app itself, never on the invitation path.
            redirectTo: '/',
          })}
        >
          <GoogleMark />
          Continue with Google
        </a>
      )}

      <p className="muted small">
        <button className="link" onClick={() => setShowToken((v) => !v)}>
          {showToken ? 'Hide' : 'Use an API token instead'}
        </button>
      </p>

      {showToken && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!value.trim()) return;
            setToken(value.trim());
            setValue('');
            onTokenSaved();
          }}
        >
          <p className="muted small">
            For development and scripts. Mint one with <code>pnpm token:mint</code>.
          </p>
          <input
            type="password"
            value={value}
            placeholder="pln_…"
            autoComplete="off"
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="submit">Save token</button>
        </form>
      )}
    </div>
  );
}

/**
 * Google's multicolour "G", inline so the sign-in screen loads no third-party asset.
 *
 * Decoration: the link's words are its accessible name, so the mark is hidden from assistive
 * technology rather than labelled a second time.
 */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Shown when a token is present but rejected, so the user is not stuck. */
export function SignInError({ error, onReset }: { error: ApiError; onReset: () => void }) {
  return (
    <div className="card">
      <h2>That did not work</h2>
      <p className="muted">
        {error.status} · {error.code} — {error.message}
      </p>
      <button onClick={onReset}>Start again</button>
    </div>
  );
}
